// janus-engine/charges/combat-refill.ts — R-115 (2026-09-30): the per-combat charge refill, decided in the browser.
//
// The rule (was PHP reset_recharge_range_end_abilities_for_encounter_creatures(), functions.php): at combat START and EXIT, every
// charged ability a combat member holds that carries `recharge_range_end` (the start-of-turn recharge roll family, e.g. Drinker of
// Pain) or `ability_resets_each_combat` (no roll — "the first time you would fall this combat", Love Defies Death Effect) goes back
// to its max. What this module adds over the PHP loop is the STATUS-GRANTED helper: an aura/item-as-status row attaches its
// `passive_ability` children (Love Defies Death → Love Defies Death Effect) to the holder, but a client-truth lane patches the status
// row in without a recompile, so the child is often missing from the held bucket. The planner reads it off the held status row's
// definition instead, so the refill no longer depends on whether a compile happened to run after the aura pass.
//
// Pure: rows + readers in, `{holder, item_id, to}` ops out. The combat-truth lane (websocket/combat-truth.ts) posts them; PHP
// (combat_truth_commit_inc.php) checks authority and saves. Nothing here reads the DOM, the network or the clock.
import { CHARGE_BUCKETS, abilityEntryId, type ChargeReaders } from './charge-adjust.js';

type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const scalar = (v: unknown): unknown => (Array.isArray(v) ? v[0] : v);
/** PHP (int) of a numeric scalar, null when PHP is_numeric() would be false (an 'NA' / variable-tag max never refills). */
const numeric = (v: unknown): number | null => {
  const s = scalar(v);
  if (typeof s === 'number') return Number.isFinite(s) ? Math.trunc(s) : null;
  return typeof s === 'string' && /^\s*[+-]?(\d+\.?\d*|\.\d+)\s*$/.test(s) ? Math.trunc(Number(s)) : null;
};

/** AbilityType::getAll() — the refill's own bucket walk (it never added innate_spell_per_long_rest, unlike change_charges()). */
export const REFILL_BUCKETS = CHARGE_BUCKETS.filter(b => b !== 'innate_spell_per_long_rest');
const MAX_MODIFIER_LAYERS = ['increase_max_charges_of_ability', 'decrease_max_charges_of_ability'] as const;

export interface RefillReaders extends ChargeReaders { definition: (id: string) => Row | null }
export interface RefillOp { holder: string; item_id: string; to: number }
export interface MemberRefill {
  ops: RefillOp[];
  /** Definition ids the page does not hold — the caller fetches them and plans again (never guesses). */
  missing: string[];
  /** Status-granted helpers skipped because a held passive / feat may move their max (the compiled max is not on the page). */
  skipped: Array<{ item_id: string; reason: string }>;
}

/** `layer_has(recharge_range_end) || layer_value(ability_resets_each_combat)` — the refill predicate. */
export function refillsEachCombat(definition: Row | null, readers: ChargeReaders): boolean {
  if (!definition) return false;
  const recharge = readers.value(definition, 'recharge_range_end');
  const resets = readers.value(definition, 'ability_resets_each_combat');
  return (recharge !== null && recharge !== undefined) || (resets !== null && resets !== undefined && resets !== '' && resets !== 0 && resets !== '0' && resets !== false);
}

/** The entry's own definition: an inline item_arr with layers, else the catalog definition the page holds. */
function entryDefinition(entry: unknown, id: string, readers: RefillReaders): Row | null {
  const inline = asRow(asRow(entry)?.item_arr);
  if (inline && asRow(inline.item_layer_types)) return inline;
  return readers.definition(id);
}

/** A status row's definition: the shell the row carries (a truth frame ships one on every NEW row), else the catalog. */
function statusDefinition(status: Row, readers: RefillReaders): { id: string; def: Row | null } {
  const shell = asRow(status.status_arr);
  const id = upper(scalar(status.status_item_id) ?? asRow(shell?.items)?.item_id ?? shell?.item_id);
  if (shell && asRow(shell.item_layer_types) && Object.keys(shell.item_layer_types as Row).length) return { id, def: shell };
  return { id, def: id ? readers.definition(id) : null };
}

/** Whether a held passive / feat carries a max-charge modifier that targets `id` (or targets nothing readable): 'unknown' while any
 * carrier's definition is not held — never read as "unmodified" (a base-max write could then LOWER a modified pool). */
function maxModified(row: Row, id: string, readers: RefillReaders, missing: Set<string>): 'yes' | 'no' | 'unknown' {
  let unknown = false;
  for (const bucket of ['passive_ability', 'feat']) {
    for (const value of readers.entries(row, bucket)) {
      const carrierId = abilityEntryId(value);
      const carrier = entryDefinition(value, carrierId, readers);
      if (!carrier) { unknown = true; if (carrierId) missing.add(carrierId); continue; }
      for (const layer of MAX_MODIFIER_LAYERS) {
        const targets = readers.entries(carrier, layer);
        if (!targets.length) continue;
        const ids = targets.map(t => upper(typeof t === 'string' ? t : asRow(asRow(t)?.items)?.item_id ?? asRow(t)?.item_id));
        if (ids.includes(id) || ids.some(t => !t)) return 'yes';
      }
    }
  }
  return unknown ? 'unknown' : 'no';
}

/**
 * One member's refill.
 *   bucket  — the PHP loop, entry for entry: `ability_entry_charges(e, 0) < ability_entry_max_charges(e, 0)` → max. The current falls
 *             back to the definition's ability_charges exactly as ability_entry_charges() does (so an entry with no count never refills).
 *   granted — passives the member's held status rows attach (the status definition's `passive_ability` children, and the status item
 *             itself when it is a charged ability), minus anything already in a bucket. The page holds no count for them, so the op is
 *             always "set to max"; the save is a no-op when the row already sits there. Max = the definition's numeric ability_charges.
 */
export function planMemberRefill(row: Row, readers: RefillReaders, include: { bucket: boolean; granted: boolean }): MemberRefill {
  const holder = upper(asRow(row.encounter_creatures)?.encounter_creature_id);
  const ops: RefillOp[] = [];
  const missing = new Set<string>();
  const skipped: MemberRefill['skipped'] = [];
  const seen = new Set<string>();
  if (!holder) return { ops, missing: [], skipped };
  // Every representation is tested, like the PHP loop; change_charges() then writes the pool clamped by the FIRST entry carrying the id.
  const first = new Map<string, unknown>();
  const heldMaxOf = (value: unknown): unknown => {
    const entry = asRow(value); const charges = asRow(entry?.charges);
    return charges && 'max' in charges ? charges.max : entry && 'max_ability_charges' in entry ? entry.max_ability_charges : undefined;
  };
  for (const bucket of REFILL_BUCKETS) {
    for (const value of readers.entries(row, bucket)) {
      const id = abilityEntryId(value);
      if (!id) continue;
      seen.add(id);
      if (!first.has(id)) first.set(id, value);
      if (!include.bucket || ops.some(op => op.item_id === id)) continue;
      const entry = asRow(value);
      const charges = asRow(entry?.charges);
      const heldCurrent = charges && 'current' in charges ? charges.current : entry && 'ability_charges' in entry ? entry.ability_charges : undefined;
      // No count on the entry: ability_entry_charges() falls back to the layer ability_charges — the max — so it never refills.
      if (heldCurrent === undefined) continue;
      const heldMax = heldMaxOf(value);
      if (heldMax !== undefined && numeric(heldCurrent) !== null && numeric(heldMax) !== null && !(numeric(heldCurrent)! < numeric(heldMax)!)) continue;
      const def = entryDefinition(value, id, readers);
      if (!def) { missing.add(id); continue; }
      if (!refillsEachCombat(def, readers)) continue;
      const max = numeric(heldMax !== undefined ? heldMax : readers.value(def, 'ability_charges'));
      const current = numeric(heldCurrent);
      if (max === null || current === null || !(current < max)) continue;
      const firstHeld = heldMaxOf(first.get(id));
      const clamp = numeric(firstHeld !== undefined ? firstHeld : readers.value(def, 'ability_charges'));
      ops.push({ holder, item_id: id, to: Math.max(0, clamp === null ? max : Math.min(max, clamp)) });
    }
  }
  if (include.granted) {
    const statuses = asRow(row.statuses)?.target;
    for (const status of Array.isArray(statuses) ? statuses : []) {
      const s = asRow(status);
      if (!s) continue;
      if (upper(scalar(s.status_target_id)) && upper(scalar(s.status_target_id)) !== holder) continue;
      const { id: statusItem, def } = statusDefinition(s, readers);
      if (!def) { if (statusItem) missing.add(statusItem); continue; }
      const candidates: Array<{ id: string; def: Row | null }> = [{ id: statusItem, def }];
      for (const child of readers.entries(def, 'passive_ability')) {
        const cid = abilityEntryId(child);
        if (cid) candidates.push({ id: cid, def: entryDefinition(child, cid, readers) });
      }
      for (const c of candidates) {
        if (!c.id || seen.has(c.id)) continue;
        if (!c.def) { missing.add(c.id); continue; }
        if (!refillsEachCombat(c.def, readers)) continue;
        const max = numeric(readers.value(c.def, 'ability_charges'));
        if (max === null) continue;
        const modified = maxModified(row, c.id, readers, missing);
        if (modified === 'unknown') continue; // the caller fetches the carriers and plans again; still unknown → never written
        seen.add(c.id);
        if (modified === 'yes') { skipped.push({ item_id: c.id, reason: 'max-charge-modifier' }); continue; }
        ops.push({ holder, item_id: c.id, to: Math.max(0, max) });
      }
    }
  }
  return { ops, missing: [...missing], skipped };
}

/** The encounter's active combat members (ft_creature_matches_active_combat): a tagged row on the active combat id; a wholly untagged
 * legacy combat counts every resident. */
export function activeCombatMembers(encounter: Row | null): Row[] {
  const roster = Array.isArray(encounter?.encounter_creatures_arr) ? (encounter!.encounter_creatures_arr as unknown[]).map(asRow).filter((r): r is Row => !!r) : [];
  const bucket = asRow(encounter?.encounters);
  const active = upper(scalar(bucket && 'active_combat_id' in bucket ? bucket.active_combat_id : encounter?.active_combat_id));
  const tag = (r: Row): string => upper(scalar(asRow(r.encounter_creatures)?.combat_id));
  if (active) return roster.filter(r => tag(r) === active);
  return roster.some(r => tag(r)) ? [] : roster;
}

/** Plan a whole boundary over the given members; ops deduped per (holder, item). */
export function planCombatRefill(members: Row[], readers: RefillReaders, include: { bucket: boolean; granted: boolean }): MemberRefill {
  const out: MemberRefill = { ops: [], missing: [], skipped: [] };
  const keys = new Set<string>();
  const missing = new Set<string>();
  for (const row of members) {
    const plan = planMemberRefill(row, readers, include);
    for (const op of plan.ops) { const k = op.holder + ':' + op.item_id; if (!keys.has(k)) { keys.add(k); out.ops.push(op); } }
    plan.missing.forEach(id => missing.add(id));
    out.skipped.push(...plan.skipped);
  }
  out.missing = [...missing];
  return out;
}
